import { useEffect, useRef, useState } from 'react'
import './CkEditorField.css'
import { Base64UploadAdapterPlugin, TOOLBAR_ITEMS, loadClassicEditor } from '../lib/ckeditor'
import { isEnglish } from '../lib/i18n'
import { awarenessAttachmentUrl } from '../lib/media'

/**
 * The advanced editor — CKEditor 5, the same build the legacy authors templates in.
 *
 * `PhishingTemplates/js.cshtml` and `Awareness/js.cshtml` both run `ClassicEditor.create(...)`
 * with the same toolbar; this is that editor, mounted imperatively over a host element
 * because the React wrapper for this line does not accept React 19.
 *
 * IT LOADS THE LEGACY'S OWN BUNDLE, not the npm one — see `src/lib/ckeditor.js` for why.
 * The short version: the npm build has no source editing and no full-document support, so it
 * silently ate the doctype, `<head>` and `<style>` of every phishing template that passed
 * through it.
 *
 * The instance is created ONCE and fed from outside through `setData`. Re-creating an editor
 * per keystroke would take the caret with it.
 *
 * @param {string} value  HTML — a fragment or a complete document
 * @param {(html: string) => void} onChange
 * @param {number} [minHeight]  the editable's floor, in pixels
 * @param {(editor: object) => void} [onReady]  the CKEditor instance once it exists, for
 *   a caller that has to act on the document from outside — insert at the caret, focus
 */
export default function CkEditorField({ id, value = '', onChange, minHeight = 320, dir = 'rtl', onReady }) {
  const hostRef = useRef(null)
  const editorRef = useRef(null)
  const [failed, setFailed] = useState(false)
  /** `onChange` changes identity every render; the editor is built once. */
  const emit = useRef(onChange)
  emit.current = onChange
  const ready = useRef(onReady)
  ready.current = onReady

  useEffect(() => {
    const host = hostRef.current
    if (!host) return undefined
    let disposed = false

    loadClassicEditor()
      .then((ClassicEditor) =>
        ClassicEditor.create(host, {
          /* THE EDITOR'S OWN CHROME FOLLOWS THE PANEL. `ui` was pinned to 'ar', so an
             administrator working in English got an English screen with an Arabic editor
             inside it — "معالج نصوص", "فقرة", "المصدر" — on every field that uses this.
             The legacy passes its page language straight through (`ui: lang`), which is
             what this now does. `content` stays tied to `dir`: it describes the text being
             WRITTEN, not the person writing it. */
          language: { ui: isEnglish ? 'en' : 'ar', content: dir === 'rtl' ? 'ar' : 'en' },
          toolbar: { items: TOOLBAR_ITEMS, shouldNotGroupWhenFull: true },
          extraPlugins: [Base64UploadAdapterPlugin],
          /* `previewsInData: true` — the legacy stores what it shows.
           *
           * `extraProviders` is the legacy's own (`Views/Awareness/js.cshtml:127`): pasting
           * the URL of an uploaded awareness video embeds a real `<video>`, because
           * CKEditor's built-in providers only know YouTube, Vimeo and friends — a link to
           * our own file would otherwise sit there as a link. */
          mediaEmbed: {
            previewsInData: true,
            extraProviders: [
              {
                name: 'rawamAwarenessVideo',
                url: /\/AwarenessesAttachments\/([a-f0-9]+)\.mp4$/i,
                html: (match) =>
                  `<video width="640" height="360" controls><source src="${awarenessAttachmentUrl(`${match[1]}.mp4`)}" type="video/mp4"></video>`,
              },
            ],
          },
          /* LIVE NOW, where it used to be ignored. A phishing template is arbitrary mail
             markup — inline styles, `<table bgcolor>`, `<font>`, whatever the designer sent
             — and GeneralHtmlSupport is what keeps it intact. `FullPage`, in the same build,
             is what keeps the doctype and `<head>`. */
          htmlSupport: {
            allow: [{ name: /.*/, attributes: true, classes: true, styles: true }],
          },
          fontFamily: { supportAllValues: true },
          fontSize: { options: [10, 12, 14, 'default', 18, 20, 22], supportAllValues: true },
          /* The legacy nests these under `decorators`, where CKEditor never reads them.
             They are top-level `link.*` options. */
          link: { addTargetToExternalLinks: true, defaultProtocol: 'https://' },
          placeholder: '',
        }),
      )
      .then((editor) => {
        if (disposed) {
          editor.destroy()
          return
        }
        editorRef.current = editor
        editor.setData(value ?? '')
        editor.editing.view.change((writer) => {
          writer.setStyle('min-height', `${minHeight}px`, editor.editing.view.document.getRoot())
        })
        editor.model.document.on('change:data', () => emit.current?.(editor.getData()))
        ready.current?.(editor)
      })
      .catch(() => {
        if (!disposed) setFailed(true)
      })

    return () => {
      disposed = true
      const editor = editorRef.current
      editorRef.current = null
      editor?.destroy().catch(() => {})
    }
    // The editor owns its own lifecycle; `value` is pushed in below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* Pushed in, not bound: assigning on every render would fight the user's caret. Only a
     value the editor does not already hold is written — which is how an async load, or a
     switch to another template, reaches an editor that is already mounted. */
  useEffect(() => {
    const editor = editorRef.current
    if (!editor) return
    if ((value ?? '') !== editor.getData()) editor.setData(value ?? '')
  }, [value])

  /* A 1.7 MB script can fail to load. Falling back to a plain textarea keeps the content
     editable and saveable rather than trapping the work behind a blank panel. */
  if (failed) {
    return (
      <textarea
        id={id}
        className="ckfield__fallback"
        dir="ltr"
        style={{ minHeight }}
        value={value ?? ''}
        onChange={(event) => onChange?.(event.target.value)}
      />
    )
  }

  return <div id={id} ref={hostRef} className="ckfield" dir={dir} />
}
